/* eslint-disable func-names */
/* eslint-disable react/destructuring-assignment */
import React from 'react';
import {
	NODE_LI,
	render,
	NODE_PARAGRAPH,
	NODE_IMAGE,
	NODE_HEADING,
	MARK_LINK,
	MARK_TEXT_STYLE,
} from 'storyblok-rich-text-react-renderer';
import classNames from 'classnames';
import i18next from 'i18next';
import { compose, render as renderStory } from '@/utils/story-composer';
import Image from '@/components/elements/image';
import { TextEnum, ProseHeadingEnum, TextColorEnum } from '@/enums/styling';
import { getTailwindClassesByEnum } from '@/utils/cssHelpers';
import LinkComponent from '@/components/elements/link-component';
import CountryBasedPriceLabel from '@/components/elements/country-based-price-label';
import TextInputValue from '@/components/elements/text-input-value';

/**
 * Recursive function to detect if there is a src key inside the Object tree
 * The object structure goes from props -> children -> props .....
 *
 * @param { Object } element
 * @param { String } matchingKey
 */
function searchTree(element, matchingKey) {
	if (typeof element === 'string' || !element.props) {
		return false;
	}

	for (const [key, value] of Object.entries(element.props)) {
		if (key === matchingKey) {
			return true;
		}

		if (key === 'children') {
			return searchTree(value, matchingKey);
		}
	}

	return false;
}

function fixLibraryLinks(href) {
	// Rewrite all link to teacher.gynzy.com library, to public library: gynzy.com/teach
	return href.replace(/teacher.gynzy.com\/#\/([a-zA-Z-]+)\/docent\/index\/library/g, 'www.gynzy.com/teach/$1/library');
}

const config = {
	markResolvers: {
		[MARK_LINK]: (children, props) => {
			const { href, target, linktype } = props;
			if (['email', 'url'].includes(linktype) || href.match(/^(https?:)?\/\//)) {
				const externalLink = linktype === 'email' ? `mailto:${href}` : href;

				let link = fixLibraryLinks(externalLink);

				// Use relative path for gynzy.com links
				if (link.startsWith('https://www.gynzy.com/')) {
					link = link.replace('https://www.gynzy.com/', '/');
				}

				return (
					<a href={link} target={target}>
						{children}
					</a>
				);
			}

			// FIXME Should not be based on intl language
			const url = `/${i18next.language}${href}`;

			// Internal links: map to <LinkComponent>
			const link = { ...props, openNewTab: target, linktype, url, cached_url: url };
			return <LinkComponent link={link}>{children}</LinkComponent>;
		},
		[MARK_TEXT_STYLE]: children => <span>{children}</span>,
	},
	nodeResolvers: {
		[NODE_LI]: children => {
			const childProps = children?.[0].props;
			return (
				<li>
					<p className="inline">{childProps ? childProps.children : children}</p>
				</li>
			);
		},

		[NODE_HEADING]: (children, { level }) => {
			// If the user uses a styling class in storyblok, get the class and heading text to create a h-tag instead of a span
			const className = children?.[0]?.props?.className || null;
			const headingText = children?.[0]?.props?.children || null;

			return React.createElement(`h${level}`, { className }, headingText || children);
		},
		[NODE_PARAGRAPH]: children => {
			// Don't wrap images inside a Paragraph element
			let hasSourceElement = false;
			if (Array.isArray(children)) {
				hasSourceElement = children.some(child => searchTree(child, 'src'));
			}

			if (hasSourceElement === true) {
				return React.createElement('div', { className: 'overflow-hidden rounded-lg' }, children);
			}

			return children === null ? React.createElement('br', null) : React.createElement('p', null, children);
		},
	},
};

function Text({ elementProps, text, textAlign, headingAlign, textColor, elementId, pageContext }) {
	// eslint-disable-next-line react/no-unstable-nested-components
	config.nodeResolvers[NODE_IMAGE] = function (_, props) {
		return <Image {...props} pageContext={pageContext} />;
	};

	config.defaultBlokResolver = (component, props) => renderStory({ component, ...props }, pageContext);
	config.textResolver = text => {
		// allow inline-components with the following format: {{comp-name arg=value}}

		const inlineComponentRegex = /(\{\{[a-zA-Z0-9-_ =]+\}\})/gm;
		const hasInlineComponents = inlineComponentRegex.test(text);

		if (!hasInlineComponents) {
			return text;
		}

		const parts = text.split(inlineComponentRegex);
		return parts.map((p, index) => {
			// Math component name and arguments
			const match = p.match(/\{\{([a-z-]+)([a-zA-Z0-9_ =]+)*\}\}/);
			if (match) {
				const [, component, argsGroup] = match;
				const props = {
					key: index,
					pageContext,
				};

				if (argsGroup) {
					const args = argsGroup.split(' ').filter(p => p !== '');

					for (let i = 0; i < args.length; i++) {
						const [arg, value] = args[i]?.split('=') || [];
						props[arg] = value;
					}
				}

				if (component === 'country-based-price-label') {
					return React.createElement(CountryBasedPriceLabel, props);
				}
				if (component === 'text-input-value') {
					return React.createElement(TextInputValue, props);
				}
			}

			// eslint-disable-next-line react/no-array-index-key
			return <React.Fragment key={index}>{p}</React.Fragment>;
		});
	};

	const headingAlignClass = getTailwindClassesByEnum(ProseHeadingEnum, headingAlign);
	const textAlignClass = getTailwindClassesByEnum(TextEnum, textAlign);
	const fontColorClass = getTailwindClassesByEnum(TextColorEnum, textColor);

	return (
		<div
			{...elementProps}
			id={elementId}
			className={classNames(
				'prose md:prose-md 2xl:prose-2xl 3xl:prose-3xl w-full',
				{ 'scroll-mt-24': elementId }, // Add scroll-margin-top when elementId is set to offset the sticky header
				pageContext.proseContainer,
				headingAlignClass,
				textAlignClass,
				fontColorClass
			)}
		>
			{render(text, config)}
		</div>
	);
}

compose('text', Text);

export default Text;
